"use client";

import type { ReactNode } from "react";
import { CheckCircle2 } from "@/components/icons";
import { motion, useReducedMotion } from "@/lib/motion";
import { GREEN } from "@/lib/constants";
import { cn } from "@/lib/utils";
import HeroGlow from "./hero-glow";

type FormSuccessProps = {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
};

export function FormSuccess({ title, description, children, className }: FormSuccessProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center gap-4 rounded-2xl border p-6 text-center sm:p-8",
        className
      )}
      style={{
        backgroundColor: `${GREEN}14`,
        borderColor: `${GREEN}40`,
      }}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ backgroundColor: `${GREEN}26` }}
        aria-hidden="true"
      >
        <CheckCircle2 size={24} style={{ color: "var(--brand-green)" }} />
      </div>
      <h2 className="text-xl font-bold" style={{ color: "var(--brand-green)" }}>
        {title}
      </h2>
      {description && <p className="text-muted-foreground max-w-xs text-sm">{description}</p>}
      {children}
    </div>
  );
}

export function FormSuccessPage({ title, description, children }: FormSuccessProps) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-24 sm:px-6 lg:px-8"
    >
      <HeroGlow align="left" />
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <FormSuccess title={title} description={description}>
          {children}
        </FormSuccess>
      </motion.div>
    </main>
  );
}
